import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { RoutedErrorBoundary } from '@/components/ErrorBoundary'
import { ScrollToTop } from '@/components/layout/ScrollToTop'
import { AuthGuard } from '@/components/guards/AuthGuard'
import { GuestGuard } from '@/components/guards/GuestGuard'
import { AppLayout } from '@/components/layout/AppLayout'
import { useAuthStore } from '@/store/authStore'
import type { User } from '@/types/auth'
import { LoginPage } from '@/pages/auth/LoginPage'
import { EmailVerificationPage } from '@/pages/auth/EmailVerificationPage'
import { GoogleCallbackPage } from '@/pages/auth/GoogleCallbackPage'
import { PasswordResetPage } from '@/pages/auth/PasswordResetPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { LearningSessionPage } from '@/pages/learn/LearningSessionPage'
import { ReviewSessionPage } from '@/pages/review/ReviewSessionPage'
import { VocabularyPage } from '@/pages/vocabulary/VocabularyPage'
import { WordDetailPage } from '@/pages/vocabulary/WordDetailPage'
import { ArticlesPage } from '@/pages/reading/ArticlesPage'
import { AdminCreateArticlePage } from '@/pages/reading/AdminCreateArticlePage'
import { AdminFeatureFlagsPage } from '@/pages/admin/AdminFeatureFlagsPage'
import { AdminMetricsPage } from '@/pages/admin/AdminMetricsPage'
import { AdminHubPage } from '@/pages/admin/AdminHubPage'
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage'
import { AdminFeedbackPage } from '@/pages/admin/AdminFeedbackPage'
import { ArticleReaderPage } from '@/pages/reading/ArticleReaderPage'
import { RecommendedReadingPage } from '@/pages/reading/RecommendedReadingPage'
import { AdminGuard } from '@/components/guards/AdminGuard'
import { AiPracticePage } from '@/pages/ai/AiPracticePage'
import { TestsPage } from '@/pages/tests/TestsPage'
import { TestSessionPage } from '@/pages/tests/TestSessionPage'
import { ProgressPage } from '@/pages/progress/ProgressPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { OnboardingGuard } from '@/components/guards/OnboardingGuard'
import { WelcomeOnboardingPage } from '@/pages/onboarding/WelcomeOnboardingPage'
import { LearningDemoPage } from '@/pages/onboarding/LearningDemoPage'
import { LandingPage } from '@/pages/landing/LandingPage'
import { CatalogPage } from '@/pages/catalog/CatalogPage'
import { PrivacyPolicyPage } from '@/pages/legal/PrivacyPolicyPage'
import { TermsOfServicePage } from '@/pages/legal/TermsOfServicePage'
import { FaqPage } from '@/pages/faq/FaqPage'

declare global {
  interface Window {
    /** Set by MOB-24 lighthouse baseline script only; inert in normal browsing. */
    __LEXIO_AUTH_BOOTSTRAP__?: { accessToken: string; user: User }
  }
}

const authBootstrap = window.__LEXIO_AUTH_BOOTSTRAP__
if (authBootstrap?.accessToken && authBootstrap?.user) {
  useAuthStore.getState().setAuth(authBootstrap.accessToken, authBootstrap.user)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* RR7 defaults to startTransition for history updates; deferred UI can stick
          while the address bar already changed (menu clicks look broken). */}
      <BrowserRouter useTransitions={false}>
        <ScrollToTop />
        <RoutedErrorBoundary>
          <Routes>
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/faq" element={<FaqPage />} />

            <Route element={<GuestGuard />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
              <Route path="/reset-password" element={<PasswordResetPage />} />
            </Route>

            <Route element={<AuthGuard />}>
              <Route element={<OnboardingGuard />}>
                <Route path="/welcome" element={<WelcomeOnboardingPage />} />
                <Route path="/learning-demo" element={<LearningDemoPage />} />
                <Route element={<AppLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/catalog" element={<CatalogPage />} />
                <Route path="/vocabulary" element={<VocabularyPage />} />
                <Route path="/vocabulary/:id" element={<WordDetailPage />} />
                <Route path="/reading" element={<ArticlesPage />} />
                <Route element={<AdminGuard />}>
                  <Route path="/admin" element={<AdminHubPage />} />
                  <Route path="/admin/articles/create" element={<AdminCreateArticlePage />} />
                  <Route path="/admin/flags" element={<AdminFeatureFlagsPage />} />
                  <Route path="/admin/metrics" element={<AdminMetricsPage />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
                  <Route path="/reading/create" element={<Navigate to="/admin/articles/create" replace />} />
                </Route>
                <Route path="/reading/recommended" element={<RecommendedReadingPage />} />
                <Route path="/reading/:id" element={<ArticleReaderPage />} />
                <Route path="/learn/:sessionId" element={<LearningSessionPage />} />
                <Route path="/review" element={<ReviewSessionPage />} />
                <Route path="/tests" element={<TestsPage />} />
                <Route path="/tests/:id" element={<TestSessionPage />} />
                <Route path="/progress" element={<ProgressPage />} />
                <Route path="/ai-practice" element={<AiPracticePage />} />
                <Route path="/ai-tutor" element={<Navigate to="/dashboard" replace />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </RoutedErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
